import { SlimBanner as ModeSlimBanner } from '@stitch-fix/mode-react';
import type { HeaderProps } from '@stitch-fix/knit';
import { useState } from 'react';
import { BannerIcon, type BannerIconProps } from './BannerIcon';
import { isNonEmptyString } from '../../utils/helpers';
import { SlimBannerModal, type SlimBannerModalProps } from './SlimBannerModal';
import { isEmpty } from '../JsonRteRenderer/JsonRteRenderer';
import type { LayoutProps } from '../Layout';

export type SlimBannerProps = {
  text: string;
  icon: BannerIconProps['icon'] | null;
  variant: NonNullable<Required<HeaderProps['slimBanner']>>['variant'];
  cta: {
    text: string;
    href: string;
  };
  modal: Omit<SlimBannerModalProps, 'isOpen' | 'close'>;
};

export const shouldRenderSlimBannerModal = (
  modal?: SlimBannerProps['modal'],
) => {
  if (!modal) {
    return false;
  }

  return (
    !!modal.image.cloudinaryAsset ||
    !isEmpty(modal.heading.children) ||
    !isEmpty(modal.body.children)
  );
};

export const SlimBanner = ({
  text,
  icon,
  variant,
  cta,
  modal,
}: SlimBannerProps) => {
  const shouldRenderModal = shouldRenderSlimBannerModal(modal);

  const shouldRenderModeCta =
    isNonEmptyString(cta.href) && isNonEmptyString(cta.text);

  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);

  const modalCta: React.ComponentProps<typeof ModeSlimBanner>['cta'] = {
    text: 'See details',
    onClick: () => setIsOpen(true),
  };

  const knitCta: React.ComponentProps<typeof ModeSlimBanner>['cta'] = {
    url: cta.href,
    text: cta.text,
  };

  const getCtaProps = () => {
    if (shouldRenderModal) {
      return modalCta;
    }
    if (shouldRenderModeCta) {
      return knitCta;
    }

    return undefined;
  };

  return (
    <>
      <ModeSlimBanner
        text={text}
        icon={icon ? <BannerIcon icon={icon} /> : null}
        variant={variant}
        cta={getCtaProps()}
      />
      {shouldRenderModal && (
        <SlimBannerModal
          isOpen={isOpen}
          close={close}
          heading={modal.heading}
          body={modal.body}
          image={modal.image}
          cta={modal.cta}
        />
      )}
    </>
  );
};

export const toLayoutSlimBannerProps = ({
  text,
  icon,
  variant,
  cta,
  modal,
}: SlimBannerProps): NonNullable<LayoutProps['customSlimBanner']> => {
  const includeCta = isNonEmptyString(cta.href) && isNonEmptyString(cta.text);

  return {
    id: text.split(' ').join('-'),
    text,
    icon: icon ? <BannerIcon icon={icon} /> : null,
    variant,
    ...(includeCta && {
      cta: {
        url: cta.href,
        text: cta.text,
      },
    }),
    modal: {
      heading: modal.heading,
      body: modal.body,
      image: modal.image,
      cta: modal.cta,
    },
  };
};
